import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FaStar } from 'react-icons/fa';
import SearchBar from '../components/SearchBar';
import { getSignedUrl } from '../utils/getSignedUrl';
import { getAllSongs } from '../utils/songUtils';
import { collectionDescriptions } from '../constants/collectionConfig';

// Styled Components
const PageContainer = styled.div`
  padding: 40px 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #E3E3E3;
  width: 100%;
  box-sizing: border-box;
  border-radius: 20px 20px 0 0;

  @media (max-width: 1024px) {
    padding: 40px 40px 20px;
  }

  @media (max-width: 480px) {
    padding: 30px 20px 15px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - 40px);
  max-width: 1200px;
  margin-top: 20px;
  margin-bottom: 0;

  @media (max-width: 480px) {
    width: calc(100% - 20px);
  }
`;

const Title = styled.h1`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin-bottom: 20px;
  font-size: 36px;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 15px;
  }
`;

const Description = styled.div`
  font-family: 'Rubik', sans-serif;
  color: #2E2E2E;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;

  p {
    margin: 0;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const SearchBarContainer = styled.div`
  width: 100%;
  margin-top: -60px;
  margin-bottom: 40px;
`;

const CollectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 15px;
    padding: 0 10px;
  }
`;

const CollectionCard = styled(Link)`
  position: relative;
  aspect-ratio: 1;
  border-radius: 15px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: #2E2E2E;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  }
`;

const CardPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #3A3A3A;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BlurBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$loaded ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  filter: blur(10px) brightness(0.6);
  transform: scale(1.1);
  transition: opacity 0.3s ease;
  opacity: ${props => props.$loaded ? 1 : 0};
`;

const CollectionTitle = styled.h2`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  color: white;
  font-family: 'DM Serif Text', serif;
  font-size: 24px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  z-index: 2;
  width: 80%;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CollectionType = styled.div`
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const FeaturedPill = styled.div`
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

  svg {
    margin-right: 5px;
    font-size: 12px;
    position: relative;
    top: -1px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 8px;

    svg {
      font-size: 10px;
      margin-right: 3px;
      top: 2px;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Sentinel = styled.div`
  height: 1px;
`;

const EmptyMessage = styled.p`
  font-family: 'Rubik', sans-serif;
  color: #6A7D8B;
  font-size: 18px;
  margin-top: 30px;
  text-align: center;
`;

const CollectionCardItem = ({ collection }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  // Fetch signed URL only when this component mounts
  useEffect(() => {
    const loadImage = async () => {
      if (collection.image) {
        try {
          const url = await getSignedUrl(collection.image);
          setImageUrl(url);
          
          // Preload the image
          const img = new Image();
          img.onload = () => setImageLoaded(true);
          img.src = url;
        } catch (error) {
          console.error('Error loading image:', error);
          // Show card without image on error
          setImageLoaded(true);
        }
      } else {
        // No image to load
        setImageLoaded(true);
      }
    };
    
    loadImage();
  }, [collection.image]);

  return (
    <CollectionCard to={collection.url}>
      {!imageLoaded && (
        <CardPlaceholder>
          <LoadingSpinner />
        </CardPlaceholder>
      )}
      <BlurBackground $image={imageUrl} $loaded={imageLoaded} />
      {collection.featured && (
        <FeaturedPill>
          <FaStar />
          Featured
        </FeaturedPill>
      )}
      <CollectionTitle>{collection.name}</CollectionTitle>
      <CollectionType>
        {collection.type === 'prompter' ? 'CURATOR' : 'THEME'}
      </CollectionType>
    </CollectionCard>
  );
};

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [filteredCollections, setFilteredCollections] = useState([]);
  const [visibleCollections, setVisibleCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observer = useRef();
  const sentinelRef = useRef();

  const collectionsPerLoad = 9;

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        // Get all songs to find unique categories and get a random image for each
        const songs = await getAllSongs();
        
        // Extract unique categories and prompters from song tags
        const categoriesMap = new Map();
        const promptersMap = new Map();
        
        // Featured prompters
        const featuredPrompters = ['Human Underscore'];
        
        songs.forEach(song => {
          // Handle regular categories (tags)
          song.song_tags.forEach(tag => {
            if (!categoriesMap.has(tag)) {
              // Find a random song with album art for this category
              const songsWithArt = songs.filter(s => 
                s.song_tags.includes(tag) && s.song_art
              );
              const randomSong = songsWithArt[Math.floor(Math.random() * songsWithArt.length)];
              
              categoriesMap.set(tag, {
                name: tag,
                featured: collectionDescriptions[tag]?.featured || false,
                image: randomSong?.song_art || null,
                type: 'category',
                description: collectionDescriptions[tag]?.description || null
              });
            }
          });

          // Handle prompter categories
          if (song.song_prompter && !promptersMap.has(song.song_prompter)) {
            // Only add prompter if it's featured
            if (featuredPrompters.includes(song.song_prompter)) {
              promptersMap.set(song.song_prompter, {
                name: song.song_prompter,
                featured: true,
                image: song.song_art || null,
                type: 'prompter',
                description: collectionDescriptions[song.song_prompter]?.description || null
              });
            }
          }
        });

        // Combine both maps into one array
        const collectionsArray = [
          ...Array.from(categoriesMap.values()),
          ...Array.from(promptersMap.values())
        ].sort((a, b) => {
          // Sort by featured status first
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          // Then sort alphabetically
          return a.name.localeCompare(b.name);
        });

        // Prepare collections with URL paths but don't fetch signed URLs yet
        const collectionsWithPaths = collectionsArray.map(collection => ({
          ...collection,
          // Format the URL based on collection type
          url: collection.type === 'prompter' 
            ? `/by-${collection.name.toLowerCase().replace(/\s+/g, '-')}` 
            : `/${collection.name.toLowerCase()}`
        }));

        setCollections(collectionsWithPaths);
        setFilteredCollections(collectionsWithPaths);
        setVisibleCollections(collectionsWithPaths.slice(0, collectionsPerLoad));
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  const loadMoreCollections = useCallback(() => {
    if (loadingMore || visibleCollections.length >= filteredCollections.length) return;
    
    setLoadingMore(true);
    
    // Simulate a small delay for smoother UX
    setTimeout(() => {
      setVisibleCollections(prev => {
        const nextCollections = filteredCollections.slice(
          prev.length,
          prev.length + collectionsPerLoad
        );
        return [...prev, ...nextCollections];
      });
      setLoadingMore(false);
    }, 300);
  }, [filteredCollections, visibleCollections.length, loadingMore]);

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        visibleCollections.length < filteredCollections.length
      ) {
        loadMoreCollections();
      }
    });

    if (sentinelRef.current) {
      observer.current.observe(sentinelRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loading, visibleCollections, filteredCollections, loadMoreCollections]);

  const handleSearch = (query) => {
    const lowercaseQuery = query.toLowerCase();
    
    const filtered = collections.filter(collection =>
      collection.name.toLowerCase().includes(lowercaseQuery)
    );
    
    setFilteredCollections(filtered);
    setVisibleCollections(filtered.slice(0, collectionsPerLoad));
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Collections | Tempo Stock</title>
        <meta name="description" content="Browse our curated collections of royalty-free music on Tempo Stock." />
      </Helmet>

      <HeaderContainer>
        <Title>Collections</Title>
        <Description>
          Discover our curated collections of royalty-free music, each carefully crafted to match your creative needs.
        </Description>
        <SearchBarContainer>
          <SearchBar 
            onSearch={handleSearch}
            placeholder="Search collections..."
          />
        </SearchBarContainer>
      </HeaderContainer>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <LoadingSpinner style={{ margin: '0 auto' }} />
          <p style={{ marginTop: '20px' }}>Loading collections...</p>
        </div>
      ) : (
        <>
          {filteredCollections.length > 0 ? (
            <>
              <CollectionsGrid>
                {visibleCollections.map((collection) => (
                  <CollectionCardItem 
                    key={collection.name}
                    collection={collection}
                  />
                ))}
              </CollectionsGrid>
              
              {visibleCollections.length < filteredCollections.length && (
                <>
                  <Sentinel ref={sentinelRef} />
                  {loadingMore && (
                    <EmptyMessage>Loading more collections...</EmptyMessage>
                  )}
                </>
              )}
            </>
          ) : (
            <EmptyMessage>No collections found matching your search.</EmptyMessage>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default CollectionsPage; 